import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { applyPublicTenant } from '@/utils/publicTenant';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Wrench, Loader2, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fullUrl = (p) => (p && p.startsWith('http') ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`);

const PublicWorkshopApprovalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // 'approved' | 'rejected'
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    applyPublicTenant();
    try {
      const r = await axios.get(`${API}/public/workshop/approval/${token}`);
      setData(r.data);
      if (['approved', 'rejected'].includes(r.data.status)) setDone(r.data.status);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Freigabe nicht gefunden.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (approved) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/workshop/approval/${token}/decision`, { approved, note: note.trim() });
      setDone(approved ? 'approved' : 'rejected');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übermittlung fehlgeschlagen'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Lädt…</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600" data-testid="approval-public-error">{error}</div>;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="approval-public-page">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-orange-600 text-white px-6 py-5">
          {data.practice ? <div className="text-sm opacity-90">{data.practice}</div> : null}
          <div className="text-xl font-bold flex items-center gap-2"><Wrench size={22} /> Freigabe Zusatzarbeiten</div>
          <div className="text-sm opacity-90 mt-1">{data.title}{data.plate ? ` · ${data.plate}` : ''}</div>
        </div>

        <div className="p-6 space-y-5">
          {data.description && <p className="text-slate-700 text-sm whitespace-pre-line" data-testid="approval-description">{data.description}</p>}

          {(data.photos || []).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="approval-photos">
              {data.photos.map((p, i) => (
                <button key={i} type="button" onClick={() => setLightbox(fullUrl(p))} className="relative rounded-lg overflow-hidden border group">
                  <img src={fullUrl(p)} alt={`Foto ${i + 1}`} className="w-full h-28 object-cover group-hover:opacity-90 transition-opacity" data-testid={`approval-photo-${i}`} />
                  <span className="absolute bottom-1 right-1 bg-black/50 text-white rounded-full p-1"><Camera size={12} /></span>
                </button>
              ))}
            </div>
          )}

          {(data.items || []).length > 0 && (
            <div className="rounded-xl border divide-y" data-testid="approval-items">
              {data.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-slate-700">{it.label}</span>
                  <span className="font-medium text-slate-900">CHF {Number(it.price || 0).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
                <span className="font-semibold text-slate-800">Total</span>
                <span className="font-bold text-orange-700" data-testid="approval-total">CHF {Number(data.total || 0).toFixed(2)}</span>
              </div>
            </div>
          )}

          {done ? (
            <div className={`rounded-xl p-5 text-center ${done === 'approved' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="approval-done">
              {done === 'approved' ? <CheckCircle2 className="mx-auto mb-2" /> : <XCircle className="mx-auto mb-2" />}
              <div className="font-semibold">{done === 'approved' ? 'Freigegeben – vielen Dank!' : 'Abgelehnt – vielen Dank für Ihre Rückmeldung.'}</div>
              {data.customer_note && <div className="text-sm mt-1 opacity-80">Notiz: {data.customer_note}</div>}
            </div>
          ) : (
            <div className="space-y-3">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optionale Notiz an die Werkstatt…" data-testid="approval-note" />
              <div className="flex gap-3">
                <Button disabled={submitting} onClick={() => decide(true)} data-testid="approval-approve-btn" className="flex-1 bg-green-600 hover:bg-green-700">
                  {submitting ? <Loader2 className="animate-spin" /> : <><CheckCircle2 className="mr-2 h-4 w-4" />Freigeben</>}
                </Button>
                <Button disabled={submitting} onClick={() => decide(false)} data-testid="approval-reject-btn" variant="outline" className="flex-1 border-red-300 text-red-700 hover:bg-red-50">
                  <XCircle className="mr-2 h-4 w-4" />Ablehnen
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setLightbox(null)} data-testid="approval-lightbox">
          <img src={lightbox} alt="Foto" className="max-h-[90vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default PublicWorkshopApprovalPage;
